import React, { useMemo } from 'react';
import FormSectionHeader from '../../../common/FormSectionHeader';
import DefaultExceptionFlow from '../DefaultExceptionFlow';
import { getExceptionNumberLabel, parseExceptionConfig } from '../utils';
import DefaultException from '../DefaultExceptionFlow/utils';
import ExceptionNumberInput from '../common/ExceptionNumberInput';

const BothExceptionFlow = ({
  classes,
  DispatchStore,
  editInfo,
  onChange, // ExceptionDetails.handleChange(index)(name)(value) - for individual field updates
  onChangeException, // Parent onChange('exception')(array) - for array updates
  shipmentPieces,
  isDeliveryException,
  handleRemoveException,
  isMultipleExceptionsEnabled,
  configJSON
}) => {
  const exceptions = useMemo(() => editInfo.exception || [], [editInfo.exception]);
  // exceptions array format: [{adeNumber:123,code:123,pieces:123,packageType:123,description:123},...]
  // All exceptions must have adeNumber embedded (same value across all)
  
  // Extract adeNumber from first exception (or first one that has it) for display in input field
  const adeNumberData = useMemo(() => {
    const firstWithADE = exceptions.find(ex => ex?.adeNumber != null);
    return firstWithADE || exceptions[0] || {};
  }, [exceptions]);
  
  // For display in DefaultExceptionFlow, we need to remove adeNumber (display only, data still has it)
  const defaultExceptions = useMemo(() => {
    return exceptions.map(ex => {
      const { adeNumber, ...rest } = ex;
      return rest;
    });
  }, [exceptions]);
  
  const exceptionNumberLabel = getExceptionNumberLabel(DispatchStore);
  const maxExceptionNumberLength = parseExceptionConfig(configJSON?.exceptionConfig)?.maxExceptionNumberLength;
  
  // When adeNumber changes, update ALL exceptions with the same value
  const handleADENumberChange = (value) => {
    if (exceptions.length === 0) {
      // If no exceptions exist, create one default exception with adeNumber
      const newException = DefaultException.getExceptionDetailsObject();
      newException.adeNumber = value;
      onChangeException && onChangeException('exception')([newException]);
      return;
    }
    
    // Update adeNumber in ALL existing exceptions
    const updatedExceptions = exceptions.map((ex) => ({
      ...ex,
      adeNumber: value,
      errorInfo: {
        ...(ex.errorInfo || {}),
        adeNumber: null // Clear error on change
      }
    }));
    
    onChangeException && onChangeException('exception')(updatedExceptions);
  };

  // Default flow uses same index as global (no offset needed)
  const onDefaultChange = (defaultIndex) => (name) => (value) => {
    // Use handleChange for individual field updates (it handles validation)
    onChange && onChange(defaultIndex)(name)(value);
  };

  // Add/remove should affect exceptions array
  const handleAddDefaultException = () => {
    const currentAdeNumber = adeNumberData?.adeNumber;
    const newException = DefaultException.getExceptionDetailsObject();
    // Add current adeNumber to new exception
    if (currentAdeNumber != null) {
      newException.adeNumber = currentAdeNumber;
    }
    const updatedExceptions = [...exceptions, newException];
    onChangeException && onChangeException('exception')(updatedExceptions);
  };

  const handleRemoveDefaultException = (defaultIndex) => {
    handleRemoveException && handleRemoveException(defaultIndex);
  };

  // Default flow expects editInfo.exception array (without adeNumber for display)
  // Ensure at least one row is visible for entry
  const defaultEditInfo = useMemo(() => {
    const rows =
      defaultExceptions.length > 0
        ? defaultExceptions
        : [DefaultException.getExceptionDetailsObject()];

    return {
      ...editInfo,
      exception: rows
    };
  }, [editInfo, defaultExceptions]);

  const adeNumberError = useMemo(() => adeNumberData.errorInfo?.adeNumber, [adeNumberData.errorInfo]);

  return (
    <div>
      {/* ADE Number input on top (optional) */}
      <div style={{ marginBottom: 20 }}>
        <FormSectionHeader label={exceptionNumberLabel} uppercase={false} />
        <ExceptionNumberInput
          classes={classes}
          label={exceptionNumberLabel}
          value={adeNumberData.adeNumber}
          onChange={handleADENumberChange}
          error={adeNumberError}
          maxLength={maxExceptionNumberLength}
        />
      </div>
      {/* Default flow below */}
      <div>
        <FormSectionHeader label="Exception Details" uppercase={false} />
        <DefaultExceptionFlow
          classes={classes}
          DispatchStore={DispatchStore}
          editInfo={defaultEditInfo}
          onChange={onDefaultChange}
          shipmentPieces={shipmentPieces}
          isDeliveryException={isDeliveryException}
          handleAddException={handleAddDefaultException}
          handleRemoveException={handleRemoveDefaultException}
          isMultipleExceptionsEnabled={isMultipleExceptionsEnabled}
        />
      </div>
    </div>
  );
};

export default BothExceptionFlow;
